import React, { useRef, useCallback } from 'react';
import { makeApiCall } from '../../../../utils/api';
import { API_PATHS } from '../../../../config/api-endpoints';
import { ChatMessage, GenerateTemplateRequest } from '../types';
import { Box } from '@amzn/awsui-components-console';

interface UseTemplateGenerationProps {
    setLocalMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    setWsUiState: React.Dispatch<React.SetStateAction<any>>;
}

/**
 * Hook for template generation and status polling
 */
export const useTemplateGeneration = ({
    setLocalMessages,
    setWsUiState
}: UseTemplateGenerationProps) => {
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastRequestPayloadRef = useRef<GenerateTemplateRequest | null>(null);

    // Stop polling
    const stopTemplatePolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
    }, []);

    // Handle successful template generation
    const handleTemplateSuccess = useCallback((response: any) => {
        // Remove any loading/status messages and previous template options
        setLocalMessages(prev => prev.filter(msg => 
            !msg.id.startsWith('loading-') && 
            !msg.id.startsWith('status-') &&
            !msg.id.startsWith('checking-') &&
            !msg.id.startsWith('template-options-')
        ));
        
        setWsUiState((prev: any) => ({
            ...prev,
            show_progress: false,
            input_enabled: false
        }));
        
        // Store template URI for download
        if (response.templateS3Uri) {
            window.__ADDAPT_TEMPLATE__ = {
                ...window.__ADDAPT_TEMPLATE__,
                templateUri: response.templateS3Uri,
                templateId: response.templateId
            };
        }
        
        // Create message with view/download buttons
        const successMessage: ChatMessage = {
            id: `success-${Date.now()}`,
            type: 'assistant',
            content: `**Template generated successfully!**\n\n**Template ID:** ${response.templateId}\n**Status:** VALIDATED\n\n`,
            timestamp: new Date(),
            status: 'sent',
            supportPrompts: response.templateS3Uri ? [
                {
                    id: 'view-template',
                    text: '👁️ View Template'
                },
                {
                    id: 'download-template',
                    text: '📥 Download Template'
                }
            ] : undefined
        };

        // Create options message for next steps
        const optionsMessage: ChatMessage = {
            id: `template-options-${Date.now()}`,
            type: 'assistant',
            content: `**What would you like to do next?**\n\nYou can create a job with this template, provide feedback to refine it, or upload a different file.`,
            timestamp: new Date(),
            status: 'sent',
            supportPrompts: [
                {
                    id: 'create-job-with-template',
                    text: '✅ Create Job'
                },
                {
                    id: 'provide-feedback',
                    text: '💬 Provide Feedback'
                },
                {
                    id: 'reupload-file',
                    text: '📁 Upload Excel File'
                }
            ]
        };
        
        setLocalMessages((prev: ChatMessage[]) => [...prev, successMessage, optionsMessage]);
        
        // Keep input disabled until user chooses an option
        setWsUiState((prev: any) => ({
            ...prev,
            input_enabled: false,
            input_placeholder: 'Please select an option above...',
            awaiting_job_name: false
        }));
    }, [setLocalMessages, setWsUiState]);

    // Handle validation failed
    const handleTemplateValidationFailed = useCallback((response: any) => {
        // Remove any loading/status messages first
        setLocalMessages(prev => prev.filter(msg => 
            !msg.id.startsWith('loading-') && 
            !msg.id.startsWith('status-') &&
            !msg.id.startsWith('checking-')
        ));
        
        setWsUiState((prev: any) => ({
            ...prev,
            show_progress: false
        }));
        
        // Format validation errors
        let errorMessage = '⚠️ **Template validation failed**\n\n';
        if (response.validationErrors && response.validationErrors.length > 0) {
            errorMessage += '**Validation Errors:**\n';
            response.validationErrors.forEach((error: any, index: number) => {
                errorMessage += `${index + 1}. **${error.error_type}**: ${error.message}\n`;
                if (error.field) {
                    errorMessage += `   Field: ${error.field}\n`;
                }
            });
        } else {
            errorMessage += response.errorMessage || 'Unknown validation error';
        }
        
        errorMessage += '\n\nPlease provide feedback on how to fix these errors:';
        
        setLocalMessages((prev: ChatMessage[]) => [...prev, {
            id: `validation-failed-${Date.now()}`,
            type: 'assistant',
            content: errorMessage,
            timestamp: new Date(),
            status: 'sent'
        }]);
        
        // Enable input for feedback
        setWsUiState((prev: any) => ({
            ...prev,
            input_enabled: true,
            input_placeholder: 'Describe how to fix the validation errors...',
            awaiting_feedback: true
        }));
    }, [setLocalMessages, setWsUiState]);

    // Handle generation failed
    const handleTemplateGenerationFailed = useCallback((response: any) => {
        // Remove any loading/status messages first
        setLocalMessages(prev => prev.filter(msg => 
            !msg.id.startsWith('loading-') && 
            !msg.id.startsWith('status-') &&
            !msg.id.startsWith('checking-')
        ));
        
        setWsUiState((prev: any) => ({
            ...prev,
            show_progress: false,
            chips: [] // Clear any existing chips
        }));
        
        // Show error message with action buttons as supportPrompts
        setLocalMessages((prev: ChatMessage[]) => [...prev, {
            id: `generation-failed-${Date.now()}`,
            type: 'assistant',
            content: `❌ **Template generation failed**\n\n${response.errorMessage || 'An error occurred during template generation.'}\n\nWhat would you like to do?`,
            timestamp: new Date(),
            status: 'sent',
            supportPrompts: [
                {
                    id: 'provide-feedback-generation',
                    text: '💬 Provide Feedback'
                },
                {
                    id: 'retry-generation',
                    text: '🔄 Retry Generation'
                },
                {
                    id: 'start-over',
                    text: '🔁 Start Over'
                }
            ]
        }]);
        
        // Disable input until user selects an action
        setWsUiState((prev: any) => ({
            ...prev,
            input_enabled: false
        }));
    }, [setLocalMessages, setWsUiState]);

    // Handle timeout
    const handleTemplateTimeout = useCallback(() => {
        // Remove any loading/status messages first
        setLocalMessages(prev => prev.filter(msg => 
            !msg.id.startsWith('loading-') && 
            !msg.id.startsWith('status-') &&
            !msg.id.startsWith('checking-')
        ));
        
        setWsUiState((prev: any) => ({
            ...prev,
            show_progress: false
        }));
        
        setLocalMessages((prev: ChatMessage[]) => [...prev, {
            id: `timeout-${Date.now()}`,
            type: 'assistant',
            content: '⏱️ Template generation is taking longer than expected.\n\nThe process might still be running in the background. You can try checking the status again or start over.',
            timestamp: new Date(),
            status: 'sent'
        }]);
        
        setWsUiState((prev: any) => ({
            ...prev,
            input_enabled: false,
            chips: [
                {
                    id: 'check-status',
                    text: '🔄 Check Status Again',
                    action: 'check_status'
                },
                {
                    id: 'start-over',
                    text: '🔁 Start Over',
                    action: 'reset_chat'
                }
            ]
        }));
    }, [setLocalMessages, setWsUiState]);

    // Poll template status using GetTemplateStatus API
    const startTemplateStatusPolling = useCallback(async (jobId: string) => {
        // Clear any existing polling
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
        }
        
        let pollCount = 0;
        const maxPolls = 100; // 100 * 3 seconds = 5 minutes
        
        const pollStatus = async () => {
            try {
                pollCount++;
                
                // Update progress
                const progressPercent = Math.min(90, 10 + (pollCount * 0.8));
                setWsUiState((prev: any) => ({
                    ...prev,
                    progress: {
                        current: progressPercent,
                        total: 100,
                        percentage: progressPercent,
                        label: 'Template Generation',
                        description: `Checking status...`
                    }
                }));
                
                // Call GetTemplateStatus API
                const statusResponse = await makeApiCall(API_PATHS.TEMPLATES.STATUS, 'POST', { jobId }, { skipLoading: true });
                
                // Store the template ID if available
                if (statusResponse.templateId) {
                    window.__ADDAPT_TEMPLATE__ = {
                        ...window.__ADDAPT_TEMPLATE__,
                        templateId: statusResponse.templateId,
                        status: statusResponse.status,
                        templateS3Uri: statusResponse.templateS3Uri
                    };
                }
                
                // Show current status if not null and not VALIDATED
                if (statusResponse.status && statusResponse.status !== 'VALIDATED') {
                    // Remove old status messages and loading messages
                    setLocalMessages((prev: ChatMessage[]) => {
                        const filtered = prev.filter(msg => 
                            !msg.id.startsWith('status-') && 
                            !msg.id.startsWith('loading-') &&
                            !msg.id.startsWith('checking-')
                        );
                        
                        // Add animated status message for GENERATING
                        if (statusResponse.status === 'GENERATING') {
                            return [...filtered, {
                                id: `status-${Date.now()}`,
                                type: 'assistant',
                                content: (
                                    <Box color="text-status-info">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span style={{ 
                                                display: 'inline-block',
                                                animation: 'spin 1s linear infinite',
                                                marginRight: '8px'
                                            }}>⏳</span>
                                            <span style={{
                                                animation: 'pulse 1.5s ease-in-out infinite'
                                            }}>Generating template...</span>
                                        </div>
                                        <style>{`
                                            @keyframes spin {
                                                from { transform: rotate(0deg); }
                                                to { transform: rotate(360deg); }
                                            }
                                            @keyframes pulse {
                                                0%, 100% { opacity: 1; }
                                                50% { opacity: 0.6; }
                                            }
                                        `}</style>
                                    </Box>
                                ),
                                timestamp: new Date(),
                                avatarLoading: true,
                                status: 'sent'
                            }];
                        } else {
                            return [...filtered, {
                                id: `status-${Date.now()}`,
                                type: 'system',
                                content: `📊 Template Status: ${statusResponse.status}`,
                                timestamp: new Date(),
                                status: 'sent'
                            }];
                        }
                    });
                }
                
                // Store mermaidDiagram and aiSummary if available
                if (statusResponse.mermaidDiagram || statusResponse.aiSummary) {
                    window.__ADDAPT_TEMPLATE__ = {
                        ...window.__ADDAPT_TEMPLATE__,
                        ...(statusResponse.mermaidDiagram && { mermaidDiagram: statusResponse.mermaidDiagram }),
                        ...(statusResponse.aiSummary && { aiSummary: statusResponse.aiSummary })
                    };
                }
                
                // Handle different statuses
                if (statusResponse.status === 'GENERATING') {
                    // Continue polling
                    if (pollCount >= maxPolls) {
                        clearInterval(pollIntervalRef.current!);
                        handleTemplateTimeout();
                    }
                } else if (statusResponse.status === 'VALIDATED') {
                    // Success!
                    clearInterval(pollIntervalRef.current!);
                    handleTemplateSuccess(statusResponse);
                } else if (statusResponse.status === 'VALIDATION_FAILED') {
                    // Validation failed
                    clearInterval(pollIntervalRef.current!);
                    handleTemplateValidationFailed(statusResponse);
                } else if (statusResponse.status === 'GENERATION_FAILED') {
                    // Generation failed
                    clearInterval(pollIntervalRef.current!);
                    handleTemplateGenerationFailed(statusResponse);
                }
                
            } catch (error: any) {
                console.error('GetTemplateStatus error:', error);
                // Continue polling on error unless we've exceeded max attempts
                if (pollCount >= maxPolls) {
                    clearInterval(pollIntervalRef.current!);
                    handleTemplateTimeout();
                }
            }
        };
        
        // Start polling with 10-second delay after initial call
        // First call after 10 seconds (not immediately)
        pollTimeoutRef.current = setTimeout(() => {
            pollStatus(); // First status check after 10 seconds
            // Then continue polling every 10 seconds
            pollIntervalRef.current = setInterval(pollStatus, 10000);
        }, 10000);
    }, [setWsUiState, setLocalMessages, handleTemplateSuccess, handleTemplateValidationFailed, handleTemplateGenerationFailed, handleTemplateTimeout]);

    // Generate template via API
    const generateTemplate = useCallback(async (feedback?: string) => {
        try {
            // Stop any existing polling before starting new generation
            stopTemplatePolling();
            
            // Show loading message immediately
            const loadingMessage: ChatMessage = {
                id: `loading-${Date.now()}`,
                type: 'assistant',
                content: <Box color="text-status-inactive">Generating a response</Box>,
                timestamp: new Date(),
                avatarLoading: true,
                status: 'sent'
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, loadingMessage]);
            
            const templateData = window.__ADDAPT_TEMPLATE__;
            
            // Ensure we have a jobId from GetDraftJob
            if (!templateData?.jobId) {
                throw new Error('No draft job found. Please refresh and try again.');
            }
            
            // Build request with jobId (required)
            const request: GenerateTemplateRequest = {
                jobId: templateData.jobId,
                useLegacyHandler: false,
            };
            
            // Add ONE of: questionAnswers, excelSheetUri, or feedback
            if (feedback) {
                // Scenario 3: Iterate with feedback (backend uses existing templateId from job)
                request.feedback = feedback;
            } else if (templateData?.answers) {
                // Scenario 1: Generate from Q&A
                const questionAnswersMap: Record<string, string> = {};
                templateData.answers.forEach((qa: any) => {
                    questionAnswersMap[qa.question] = qa.answer;
                });
                request.questionAnswers = questionAnswersMap;
            } else if (templateData?.fileData) {
                // Scenario 2: Generate from Excel file
                request.excelSheetUri = templateData.fileData.s3Key;
            }
            
            // Store the request payload for potential retry
            lastRequestPayloadRef.current = request;
            
            // Show progress immediately
            setWsUiState((prev: any) => ({
                ...prev,
                show_progress: true,
                input_enabled: false,
                progress: {
                    current: 10,
                    total: 100,
                    percentage: 10,
                    label: 'Template Generation',
                    description: 'Starting template generation...'
                }
            }));
            
            // Call API
            const response = await makeApiCall(API_PATHS.TEMPLATES.GENERATE, 'POST', request);
            
            // Store job ID for status polling
            window.__ADDAPT_TEMPLATE__ = {
                ...window.__ADDAPT_TEMPLATE__,
                jobId: response.jobId,
                status: response.status || 'GENERATING'
            };
            
            // Remove any loading messages first
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => !msg.id.startsWith('loading-'))
            );
            
            // Clear chips when generation starts
            setWsUiState((prev: any) => ({
                ...prev,
                chips: [],
                input_enabled: false
            }));
            
            // Show tracking message with proper status
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `template-started-${Date.now()}`,
                type: 'assistant',
                content: `✅ Template generation started!\n\n`,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Start polling for status using jobId
            startTemplateStatusPolling(response.jobId);
            
        } catch (error: any) {
            console.error('GenerateTemplate API error:', error);
            
            // Remove any loading messages
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => !msg.id.startsWith('loading-'))
            );
            
            // Check if draft job has expired
            if (error.message?.includes('Job not found') || 
                error.message?.includes('not in draft state') ||
                error.message?.includes('not found')) {
                
                setLocalMessages((prev: ChatMessage[]) => [...prev, {
                    id: `expired-draft-${Date.now()}`,
                    type: 'system',
                    content: `⏱️ **Your draft session has expired**\n\nDraft jobs are only valid for 1 hour. Please use the refresh button to start a new session.`,
                    timestamp: new Date(),
                    status: 'sent'
                }]);
                
                // Disable input
                setWsUiState((prev: any) => ({
                    ...prev,
                    show_progress: false,
                    input_enabled: false,
                    input_placeholder: 'Session expired. Please refresh to continue.'
                }));
                
            } else if (error.message?.includes('504') || error.message?.includes('timeout')) {
                // Timeout - still start polling as generation might be running
                setLocalMessages((prev: ChatMessage[]) => [...prev, {
                    id: `timeout-${Date.now()}`,
                    type: 'assistant',
                    content: `⏱️ The template generation request is taking longer than expected.\n\nThe generation is still running in the background. We'll keep checking the status...`,
                    timestamp: new Date(),
                    status: 'sent'
                }]);
                
                // Try to poll anyway if we have a jobId from previous attempt
                if (window.__ADDAPT_TEMPLATE__?.jobId) {
                    startTemplateStatusPolling(window.__ADDAPT_TEMPLATE__.jobId);
                }
            } else {
                // Generic error
                setLocalMessages((prev: ChatMessage[]) => [...prev, {
                    id: `error-${Date.now()}`,
                    type: 'system',
                    content: `❌ Failed to generate template: ${error.message}`,
                    timestamp: new Date(),
                    status: 'sent'
                }]);
                
                // Reset UI
                setWsUiState((prev: any) => ({
                    ...prev,
                    show_progress: false,
                    input_enabled: true
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setLocalMessages, setWsUiState, startTemplateStatusPolling, stopTemplatePolling]);

    // Retry the last failed template generation with the same payload
    const retryTemplateGeneration = useCallback(async () => {
        // Try to get request payload from ref first, then from window global (set by __ADDAPT_CALL_API__)
        const requestPayload = lastRequestPayloadRef.current || window.__ADDAPT_TEMPLATE__?.lastRequestPayload;
        
        if (!requestPayload) {
            console.error('No previous request to retry');
            return;
        }

        try {
            // Stop any existing polling
            stopTemplatePolling();
            
            // Show loading message
            const loadingMessage: ChatMessage = {
                id: `loading-${Date.now()}`,
                type: 'assistant',
                content: <Box color="text-status-inactive">Retrying template generation...</Box>,
                timestamp: new Date(),
                avatarLoading: true,
                status: 'sent'
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, loadingMessage]);
            
            // Show progress
            setWsUiState((prev: any) => ({
                ...prev,
                show_progress: true,
                input_enabled: false,
                progress: {
                    current: 10,
                    total: 100,
                    percentage: 10,
                    label: 'Template Generation',
                    description: 'Retrying template generation...'
                }
            }));
            
            // Call API with the stored request payload
            const response = await makeApiCall(API_PATHS.TEMPLATES.GENERATE, 'POST', requestPayload);
            
            // Store job ID for status polling
            window.__ADDAPT_TEMPLATE__ = {
                ...window.__ADDAPT_TEMPLATE__,
                jobId: response.jobId,
                status: response.status || 'GENERATING'
            };
            
            // Remove loading messages
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => !msg.id.startsWith('loading-'))
            );
            
            // Show tracking message
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `template-started-${Date.now()}`,
                type: 'assistant',
                content: `✅ Template generation retry started!\n\n`,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Start polling for status
            startTemplateStatusPolling(response.jobId);
            
        } catch (error: any) {
            console.error('Retry GenerateTemplate API error:', error);
            
            // Remove loading messages
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => !msg.id.startsWith('loading-'))
            );
            
            // Show error message
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `error-${Date.now()}`,
                type: 'system',
                content: `❌ Retry failed: ${error.message}`,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Reset UI
            setWsUiState((prev: any) => ({
                ...prev,
                show_progress: false,
                input_enabled: true
            }));
        }
    }, [setLocalMessages, setWsUiState, startTemplateStatusPolling, stopTemplatePolling]);

    return {
        generateTemplate,
        retryTemplateGeneration,
        startTemplateStatusPolling,
        stopTemplatePolling,
        handleTemplateSuccess,
        handleTemplateValidationFailed,
        handleTemplateGenerationFailed,
        handleTemplateTimeout
    };
};

